/**
 * Shared canvas painter for the Polish licence-plate generator.
 *
 * Both the /generator-tablic page and the marketing banner on the home
 * page render the plate via this single function so the visual output is
 * always identical (same proportions, EU strip width, font metrics, etc.).
 *
 * Real Polish plates are 520 × 114 mm. We render on canvas at that
 * physical ratio (approximately 4.56:1) and scale via a `scale` prop so
 * a single draw routine can produce any resolution - from a tiny 90-px
 * preview on the banner up to a 4× print-quality PNG.
 *
 * === Look & feel goals (per user brief) ===
 *   - Tall / slender plate glyphs (canvas equivalent of `transform: scaleY(1.2)`).
 *   - Wider `letter-spacing` between characters - real plates have
 *     more air around each glyph than a default sans-serif.
 *   - Embossed letters, equivalent of the CSS:
 *         text-shadow:
 *           0.5px 0.5px 0px #ffffff,
 *          -0.5px -0.5px 0px #000,
 *           2px 2px 3px rgba(0,0,0,0.3);
 *   - Reflective-aluminium plate body: vertical gradient #f8f8f8 → #e2e2e2.
 *   - Thin (2 px) glossy black outer frame + inset shadow so the edges
 *     look like a stamped, folded aluminium sheet.
 *   - EU strip flush against the LEFT edge of the plate (no white padding
 *     around it); the black outer frame wraps around the strip too.
 *   - Deeper navy blue for the EU strip and a slightly bigger, sharper
 *     admin logo (aspect ratio preserved).
 *
 * Note: "GL-Nummernschild" and "Arkisz" are commercial fonts that we
 * cannot legally embed. The font-stack below prefers them if the user
 * has them installed locally, then falls back to Google Fonts "License
 * Plate" (preloaded via /public/index.html) and finally to "Impact".
 */

const PLATE_W_MM = 520;
const PLATE_H_MM = 114;
// Real plates are typographically rendered at ~3.7 px/mm on the site - that
// gives a 1924 × 422 base render, which is close to how mobile/desktop
// browsers can display it comfortably.
const BASE_PX_PER_MM = 3.7;
export const BASE_W = Math.round(PLATE_W_MM * BASE_PX_PER_MM); // 1924
export const BASE_H = Math.round(PLATE_H_MM * BASE_PX_PER_MM); // 422

/**
 * Font stack used by the plate glyphs.
 *   1. "AdminPlateFont" - admin-uploaded custom font (highest priority
 *      when set via the admin panel).
 *   2. "Arklatrs" - bundled default plate font (/public/plate-assets/arklatrs.ttf),
 *      declared via @font-face in public/index.html so the browser has it
 *      loaded on first paint.
 *   3. "Arkisz" / "GL-Nummernschild-Eng" / "GL-Nummernschild" - commercial
 *      Polish/EU plate fonts if the user happens to have them installed.
 *   4. Google-Fonts "License Plate" + "Anton" fallbacks preloaded via
 *      index.html.
 *   5. Progressively-freer sans-serif fallbacks.
 */
const PLATE_FONT_STACK =
    '"AdminPlateFont", "Arklatrs", "Arkisz", "GL-Nummernschild-Eng", ' +
    '"GL-Nummernschild", "License Plate", "Anton", "UKNumberPlate", ' +
    '"DIN 1451 Std Mittelschrift", "Impact", "Arial Narrow", "Arial", sans-serif';

/**
 * Register an admin-uploaded font (any of .ttf / .otf / .woff / .woff2)
 * under the shared "AdminPlateFont" family so the same painter renders
 * with it on every page. Cached per URL so we don't hit the network more
 * than once during a session.
 *
 * Returns a Promise that resolves as soon as the font is loaded and
 * available to `ctx.font = ... "AdminPlateFont"`. Resolves silently even
 * on failure so callers never need to handle rejection - the font stack
 * simply falls back to the next family.
 */
const _plateFontLoadCache = new Map();
export function loadPlateFont(url) {
    if (!url) return Promise.resolve(false);
    if (_plateFontLoadCache.has(url)) return _plateFontLoadCache.get(url);
    const p = (async () => {
        if (typeof document === "undefined" || !document.fonts) return false;
        try {
            const face = new FontFace("AdminPlateFont", `url(${url})`);
            const loaded = await face.load();
            document.fonts.add(loaded);
            return true;
        } catch {
            return false;
        }
    })();
    _plateFontLoadCache.set(url, p);
    return p;
}

/**
 * Preload an image URL. Returns null if it fails to load (so we can
 * gracefully fall back to a text-only plate).
 * Uses crossOrigin='anonymous' so the resulting canvas remains
 * exportable via toDataURL.
 */
export function loadImage(url) {
    return new Promise((resolve) => {
        if (!url) return resolve(null);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

/**
 * Load an image and repaint it as a solid dark silhouette on a
 * transparent background - useful for a light line-drawing (like the
 * Polish eagle SVG/PNG) that would otherwise blend into the hologram
 * behind it. Returns a canvas (which is drawable via `drawImage` just
 * like an HTMLImageElement) with `.naturalWidth / .naturalHeight` shims
 * so it plugs into `drawPlate` unchanged.
 *
 * Threshold: any pixel darker than ~230 luminance is repainted as
 * `#0a0f2c` at full alpha (deep navy - reads as "official ink" on the
 * shiny hologram); lighter pixels stay transparent.
 */
export async function loadSilhouetteImage(url, { threshold = 210, ink = "#0a0f2c" } = {}) {
    const img = await loadImage(url);
    if (!img) return null;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return null;
    try {
        const off = document.createElement("canvas");
        off.width = w;
        off.height = h;
        const octx = off.getContext("2d");
        octx.drawImage(img, 0, 0);
        const data = octx.getImageData(0, 0, w, h);
        const px = data.data;
        // Parse ink hex into RGB once.
        const inkR = parseInt(ink.slice(1, 3), 16);
        const inkG = parseInt(ink.slice(3, 5), 16);
        const inkB = parseInt(ink.slice(5, 7), 16);
        for (let i = 0; i < px.length; i += 4) {
            const r = px[i];
            const g = px[i + 1];
            const b = px[i + 2];
            const a = px[i + 3];
            // Perceived luminance.
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;
            if (a < 20 || lum >= threshold) {
                // Background or already transparent → drop it.
                px[i + 3] = 0;
            } else {
                // Repaint as ink. Alpha scales linearly with darkness so
                // antialiased edges remain smooth (no jagged silhouette
                // outline) while solid line strokes are fully opaque.
                px[i] = inkR;
                px[i + 1] = inkG;
                px[i + 2] = inkB;
                const t = (threshold - lum) / threshold; // 0..1
                px[i + 3] = Math.min(255, Math.round(t * 340));
            }
        }
        octx.putImageData(data, 0, 0);
        // Add shims so callers can treat this canvas as an "image".
        off.naturalWidth = w;
        off.naturalHeight = h;
        return off;
    } catch {
        // Cross-origin / tainted canvas - fall back to the raw image so
        // the sticker still renders (just less visible).
        return img;
    }
}

/**
 * Paint the plate on a canvas. Everything is scaled from BASE_W × BASE_H,
 * so the same code produces preview and download at any resolution.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{
 *   prefix: string,
 *   tail: string,
 *   logoImg: HTMLImageElement|null,
 *   hologramImg?: HTMLImageElement|null,
 *   eagleImg?: HTMLImageElement|null,
 *   vintageIconImg?: HTMLImageElement|null,
 *   variant?: 'standard'|'zabytkowa',
 *   scale: number,
 * }} opts
 */
export function drawPlate(canvas, {
    prefix,
    tail,
    logoImg,
    hologramImg = null,
    eagleImg = null,
    vintageIconImg = null,
    variant = "standard",
    scale = 1,
}) {
    const isVintage = variant === "zabytkowa";
    const plateW = Math.round(BASE_W * scale);
    const plateH = Math.round(BASE_H * scale);
    const W = plateW;
    const H = plateH;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const radius = 22 * scale;

    // ---- 1. Plate body: vertical "reflective aluminium" gradient ---------
    // Exactly as specified by the user brief:
    //   linear-gradient(180deg, #f8f8f8 0%, #e2e2e2 100%)
    ctx.save();
    roundRect(ctx, 0, 0, plateW, plateH, radius);
    ctx.clip();

    const bgGrad = ctx.createLinearGradient(0, 0, 0, plateH);
    if (isVintage) {
        // Historic/vintage Polish plate: golden yellow body per the
        // reference photo. Slight vertical gradient so it still reads
        // as reflective stamped metal rather than a flat swatch.
        bgGrad.addColorStop(0, "#fbc50c");
        bgGrad.addColorStop(0.5, "#f2b900");
        bgGrad.addColorStop(1, "#e0a800");
    } else {
        bgGrad.addColorStop(0, "#f8f8f8");
        bgGrad.addColorStop(1, "#e2e2e2");
    }
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, plateW, plateH);

    // Very subtle procedural grain so the plate reads like brushed metal
    // rather than a flat CSS panel. Kept faint so the emboss shadow of
    // the glyphs still dominates.
    const grainDensity = Math.round(plateW * plateH * 0.0032);
    for (let i = 0; i < grainDensity; i++) {
        const gx = Math.random() * plateW;
        const gy = Math.random() * plateH;
        const gr = Math.random() * 0.8 * scale + 0.2 * scale;
        const dark = Math.random() < 0.5;
        ctx.fillStyle = dark
            ? `rgba(0,0,0,${0.02 + Math.random() * 0.04})`
            : `rgba(255,255,255,${0.04 + Math.random() * 0.06})`;
        ctx.beginPath();
        ctx.arc(gx, gy, gr, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();

    // ---- 2. Left EU strip - FLUSH against the plate edge ----------------
    // The strip starts at x=0 (no white padding around it) and spans the
    // full plate height. We clip to the plate's rounded rectangle so the
    // top-left / bottom-left corners of the strip inherit the plate's
    // corner radius - visually the blue "bleeds" all the way to the edge.
    // Real Polish plates: strip ≈ 40 mm out of the 520 mm plate width →
    // 148 px at BASE_PX_PER_MM=3.7.
    const stripW = 148 * scale;
    ctx.save();
    roundRect(ctx, 0, 0, plateW, plateH, radius);
    ctx.clip();

    // Solid EU-blue #133689 (per user brief) with a very subtle
    // vertical gradient so it still reads as a real, slightly reflective
    // metal-printed strip rather than a flat CSS block.
    const euGrad = ctx.createLinearGradient(0, 0, 0, plateH);
    euGrad.addColorStop(0, "#1a4098");
    euGrad.addColorStop(0.5, "#133689");
    euGrad.addColorStop(1, "#0d2a72");
    ctx.fillStyle = euGrad;
    ctx.fillRect(0, 0, stripW, plateH);

    // Barely-there diagonal shine across the strip.
    const stripSheen = ctx.createLinearGradient(0, 0, stripW, plateH);
    stripSheen.addColorStop(0, "rgba(255,255,255,0.14)");
    stripSheen.addColorStop(0.5, "rgba(255,255,255,0)");
    stripSheen.addColorStop(1, "rgba(0,0,0,0.18)");
    ctx.fillStyle = stripSheen;
    ctx.fillRect(0, 0, stripW, plateH);

    // Emblem area - admin logo bigger than before (0.72 vs 0.62) so the
    // herb reads well on the /generator-tablic preview. Aspect ratio is
    // ALWAYS preserved via naturalWidth/naturalHeight fit-inside math.
    const emblemCx = stripW / 2;
    const emblemCy = plateH * 0.32;
    const emblemSize = stripW * 0.72;
    if (logoImg && logoImg.naturalWidth > 0 && logoImg.naturalHeight > 0) {
        const iw = logoImg.naturalWidth;
        const ih = logoImg.naturalHeight;
        const s = Math.min(emblemSize / iw, emblemSize / ih);
        const dw = iw * s;
        const dh = ih * s;
        const dx = emblemCx - dw / 2;
        const dy = emblemCy - dh / 2;
        // Turn OFF smoothing for the logo blit so admin-uploaded pixel-art
        // herbs stay crisp instead of getting bilinearly-blurred.
        const prevSmooth = ctx.imageSmoothingEnabled;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(logoImg, dx, dy, dw, dh);
        ctx.imageSmoothingEnabled = prevSmooth;
    } else {
        // Fallback (no admin logo uploaded): Polish flag - a horizontal
        // two-stripe rectangle (white on top, red below) framed by a
        // subtle dark hairline so it still reads on the deep-navy EU
        // strip. This is the default visual identity per user brief;
        // any admin-uploaded logo above takes priority and hides it.
        const flagW = stripW * 0.62;
        const flagH = flagW * 0.625; // 8:5 ratio like the real PL flag
        const flagX = emblemCx - flagW / 2;
        const flagY = emblemCy - flagH / 2;
        ctx.save();
        // White upper half.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(flagX, flagY, flagW, flagH / 2);
        // Red lower half - official PL crimson (#dc143c is a good match
        // for the "amaranth red" used on the flag).
        ctx.fillStyle = "#dc143c";
        ctx.fillRect(flagX, flagY + flagH / 2, flagW, flagH / 2);
        // Thin dark border so the white half doesn't dissolve into the
        // navy strip.
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = Math.max(1, 1.2 * scale);
        ctx.strokeRect(flagX, flagY, flagW, flagH);
        // Very faint centre divider so the white/red boundary reads as
        // an intentional flag stripe rather than a printing artefact.
        ctx.strokeStyle = "rgba(0,0,0,0.18)";
        ctx.lineWidth = Math.max(0.5, 0.6 * scale);
        ctx.beginPath();
        ctx.moveTo(flagX, flagY + flagH / 2);
        ctx.lineTo(flagX + flagW, flagY + flagH / 2);
        ctx.stroke();
        ctx.restore();
    }

    // "PL" text in the lower third of the strip.
    ctx.fillStyle = "#ffffff";
    const plFontSize = stripW * 0.34;
    ctx.font = `900 ${plFontSize}px "Arial Narrow", "Arial", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PL", emblemCx, plateH * 0.8);
    ctx.restore();

    // ---- 3. Plate text - embossed black glyphs --------------------------
    // Layout: the "white area" (aluminium body to the right of the EU
    // strip) acts like a Flexbox container with:
    //   display: flex; justify-content: center; align-items: center;
    // - the text block is perfectly centered vertically AND horizontally
    // and fills as much of that free space as looks natural. To achieve
    // that on canvas we (a) compute the raw glyph widths, then (b) pick
    // a `glyphSpacing` (letter-spacing) that stretches the whole block
    // to ~90 % of the available white-area width. This makes the plate
    // "breathe" instead of huddling to one side.
    const textLeft = stripW + 24 * scale;
    // For vintage plates we reserve space on the RIGHT of the text area
    // for the vintage-car icon (per reference photo). The icon slot is
    // roughly a square, sized proportionally to the plate.
    const vintageIconH = plateH * 0.62;
    const vintageIconW = vintageIconH * 1.5; // wider than tall (car shape)
    const vintageIconRightPad = 20 * scale;
    const vintageIconSlot = isVintage
        ? vintageIconW + vintageIconRightPad + 8 * scale
        : 0;
    const textRight = plateW - 24 * scale - vintageIconSlot;
    const textAreaW = textRight - textLeft;
    const textCenterY = plateH / 2;
    // Slightly smaller base font size so that after ctx.scale(1, 1.2) the
    // glyph height still fits comfortably inside the plate.
    const glyphH = plateH * 0.58;
    ctx.font = `900 ${glyphH}px ${PLATE_FONT_STACK}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    // Raw glyph widths (no spacing) - used to solve for adaptive
    // letter-spacing below.
    const rawPrefixW = measureRawWidth(ctx, prefix);
    const rawTailW = measureRawWidth(ctx, tail);
    const gapCount =
        Math.max(0, prefix.length - 1) + Math.max(0, tail.length - 1);

    // On a real Polish plate there are TWO small marks stacked between
    // the wyróżnik and the numer (see user's reference photo): a small
    // eagle emboss on top and a smaller holographic legalization seal
    // below. Both are noticeably smaller than the glyphs so they don't
    // dominate the plate. We keep EQUAL horizontal margins on both
    // sides for symmetry.
    // The hologram is a real-world ~13×20 mm rectangle so we render it
    // slightly narrower than tall for a slim, sticker-y look
    // ("nieco chudsza" per user brief).
    // Historic (zabytkowa) plates DON'T carry the middle legalization
    // stack - instead they have a vintage-car icon on the RIGHT side.
    const hasSticker = !isVintage && !!(hologramImg || eagleImg);
    const markH = plateH * 0.3;
    const markW = markH; // eagle mark stays square
    const holoH = markH * 1.02;
    const holoW = markH * 0.72; // slimmer than the eagle above it
    const markGapV = plateH * 0.02;
    const stickerSideMargin = 18 * scale;
    const stickerSlot = hasSticker
        ? Math.max(markW, holoW) + stickerSideMargin * 2
        : 0;
    const midGap = isVintage
        ? Math.max(32 * scale, glyphH * 0.32)
        : Math.max(46 * scale, glyphH * 0.65, stickerSlot);

    // Solve for the letter-spacing that makes the text block fill
    // TARGET_FILL of the white area. Clamp within sensible bounds so a
    // very short input (e.g. "SL 12") doesn't produce absurd gaps and a
    // very long one doesn't clip against the plate frame.
    const TARGET_FILL = 0.9;
    const target = textAreaW * TARGET_FILL;
    let glyphSpacing;
    if (gapCount > 0) {
        glyphSpacing = (target - rawPrefixW - rawTailW - midGap) / gapCount;
    } else {
        glyphSpacing = 20 * scale;
    }
    // Min ≈ 15 px (per user brief: 15-20 px baseline); max caps the
    // stretch so shortest inputs still look like a plate, not a banner.
    const MIN_SPACING = 15 * scale;
    const MAX_SPACING = 55 * scale;
    glyphSpacing = Math.max(MIN_SPACING, Math.min(MAX_SPACING, glyphSpacing));

    // Recompute total width with the chosen spacing, then center the
    // block inside the white area (justify-content: center equivalent).
    const prefixW =
        rawPrefixW + Math.max(0, prefix.length - 1) * glyphSpacing;
    const tailW = rawTailW + Math.max(0, tail.length - 1) * glyphSpacing;
    let totalW = prefixW + midGap + tailW;
    // Safety: if the text would still overflow (very long custom
    // prefixes), shrink spacing further so nothing bleeds past the frame.
    if (totalW > textAreaW) {
        const overshoot = totalW - textAreaW;
        const shrinkable = Math.max(1, gapCount);
        glyphSpacing = Math.max(0, glyphSpacing - overshoot / shrinkable);
        const prefixW2 =
            rawPrefixW + Math.max(0, prefix.length - 1) * glyphSpacing;
        const tailW2 = rawTailW + Math.max(0, tail.length - 1) * glyphSpacing;
        totalW = prefixW2 + midGap + tailW2;
    }
    const finalPrefixW =
        rawPrefixW + Math.max(0, prefix.length - 1) * glyphSpacing;
    const startX = textLeft + (textAreaW - totalW) / 2;
    let cursor = startX;

    renderPlateText(ctx, prefix, cursor, textCenterY, scale, glyphSpacing);
    const midGapStart = cursor + finalPrefixW;
    cursor += finalPrefixW + midGap;
    renderPlateText(ctx, tail, cursor, textCenterY, scale, glyphSpacing);

    // ---- 3c. Vintage-car icon (only on historic "zabytkowa" plates) -----
    // Real historic Polish plates carry a small vintage-car pictogram on
    // the right side of the plate (see user reference photo). We render
    // the admin-supplied silhouette PNG right of the numer, keeping equal
    // vertical padding so it optically sits on the same baseline as the
    // glyphs.
    if (isVintage && vintageIconImg && vintageIconImg.naturalWidth > 0) {
        const iw = vintageIconImg.naturalWidth;
        const ih = vintageIconImg.naturalHeight;
        const s = Math.min(vintageIconW / iw, vintageIconH / ih);
        const dw = iw * s;
        const dh = ih * s;
        // Anchor the icon flush against the reserved right edge of the
        // plate (just inside the black frame), vertically centred.
        const dx = plateW - 24 * scale - vintageIconRightPad - dw;
        const dy = textCenterY - dh / 2;
        ctx.save();
        // Subtle emboss so the pictogram reads as printed/stamped ink
        // rather than a decal stuck on top of the yellow body.
        ctx.shadowColor = "rgba(0,0,0,0.25)";
        ctx.shadowBlur = 2 * scale;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0.5 * scale;
        ctx.drawImage(vintageIconImg, dx, dy, dw, dh);
        ctx.restore();
    }

    // ---- 3b. Legalization marks (eagle emboss on top, holo seal below) ---
    // Real Polish plates carry TWO tiny marks stacked vertically in the
    // gap between the wyróżnik and the numer (see user's reference
    // photo): a small eagle emboss on top and a smaller, slimmer
    // holographic legalization seal below. Both are much smaller than
    // the glyphs so they never dominate the plate. We keep EQUAL
    // horizontal margins on both sides for symmetry.
    if (hasSticker) {
        const stackH = markH + markGapV + holoH;
        const stackTop = textCenterY - stackH / 2;
        // The eagle mark centre-line - its container is square (markW)
        // and centred inside the mid-gap.
        const markLeft = midGapStart + (midGap - markW) / 2;
        // The hologram is slimmer, so it gets its own left-x centred
        // inside the same mid-gap → both stack children share a
        // vertical axis.
        const holoLeft = midGapStart + (midGap - holoW) / 2;

        // ---- Top mark: eagle emboss (no hologram behind it) ----
        // Reads as a dark grey embossed emblem printed directly on the
        // aluminium - smaller than the hologram to stay understated.
        const eagleTop = stackTop;
        if (eagleImg && eagleImg.naturalWidth > 0) {
            const boxW = markW * 0.95;
            const boxH = markH * 0.95;
            const iw = eagleImg.naturalWidth;
            const ih = eagleImg.naturalHeight;
            const s = Math.min(boxW / iw, boxH / ih);
            const dw = iw * s;
            const dh = ih * s;
            const dx = markLeft + (markW - dw) / 2;
            const dy = eagleTop + (markH - dh) / 2;
            ctx.save();
            // Very subtle emboss shadow so the eagle reads as physically
            // pressed into the plate.
            ctx.shadowColor = "rgba(0,0,0,0.25)";
            ctx.shadowBlur = 2 * scale;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0.5 * scale;
            ctx.drawImage(eagleImg, dx, dy, dw, dh);
            ctx.restore();
        }

        // ---- Bottom mark: holographic legalization sticker (slim) ----
        const holoTop = stackTop + markH + markGapV;
        // Generous corner radius so the sticker reads as a friendly
        // rounded rectangle rather than a hard cut-out (per user brief:
        // "dolna naklejka powinna być zaokrąglona").
        const stickerR = Math.min(holoW, holoH) * 0.24;
        ctx.save();
        // Soft drop shadow so the sticker looks physically stuck on.
        ctx.shadowColor = "rgba(0,0,0,0.3)";
        ctx.shadowBlur = 3 * scale;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 1 * scale;
        ctx.beginPath();
        roundRect(ctx, holoLeft, holoTop, holoW, holoH, stickerR);
        ctx.save();
        ctx.clip();
        if (hologramImg && hologramImg.naturalWidth > 0) {
            const iw = hologramImg.naturalWidth;
            const ih = hologramImg.naturalHeight;
            const s = Math.max(holoW / iw, holoH / ih);
            const dw = iw * s;
            const dh = ih * s;
            const dx = holoLeft + (holoW - dw) / 2;
            const dy = holoTop + (holoH - dh) / 2;
            ctx.drawImage(hologramImg, dx, dy, dw, dh);
        } else {
            const g = ctx.createLinearGradient(
                holoLeft,
                holoTop,
                holoLeft + holoW,
                holoTop + holoH,
            );
            g.addColorStop(0, "#d9d9dc");
            g.addColorStop(0.5, "#f0f0f2");
            g.addColorStop(1, "#c9c9cf");
            ctx.fillStyle = g;
            ctx.fillRect(holoLeft, holoTop, holoW, holoH);
        }
        ctx.restore(); // undo clip

        // Thin dark hairline around the holo sticker so its edges pop.
        ctx.shadowColor = "transparent";
        ctx.strokeStyle = "rgba(0,0,0,0.4)";
        ctx.lineWidth = 1 * scale;
        ctx.beginPath();
        roundRect(ctx, holoLeft, holoTop, holoW, holoH, stickerR);
        ctx.stroke();
        ctx.restore();
    }

    // ---- 4. Inset shadow around the whole plate -------------------------
    // Simulates `box-shadow: inset 0 0 6px rgba(0,0,0,0.55)` on a canvas.
    // We paint a rounded-rect stroke inside the clipped plate area with
    // shadow blur pointing outwards → the shadow bleeds into the plate
    // interior giving the "folded aluminium" edge look.
    ctx.save();
    roundRect(ctx, 0, 0, plateW, plateH, radius);
    ctx.clip();
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 8 * scale;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = "rgba(0,0,0,0.9)";
    ctx.lineWidth = 2 * scale;
    // Stroke slightly OUTSIDE the clip so only the inner half of the
    // shadow spills into the plate - creates the vignette / inset look.
    roundRect(
        ctx,
        -2 * scale,
        -2 * scale,
        plateW + 4 * scale,
        plateH + 4 * scale,
        radius + 2,
    );
    ctx.stroke();
    ctx.restore();

    // ---- 5. Bold black outer frame --------------------------------------
    // Drawn OVER everything, including the blue strip, so the frame wraps
    // around the strip too - no white gap between the strip and the frame.
    // Per user brief: chunkier black frame (was 5 px → now 8 px at scale=1)
    // so the plate reads as a properly finished pressed-aluminium piece.
    ctx.save();
    const frameW = 8 * scale;
    ctx.lineWidth = frameW;
    ctx.strokeStyle = "#000000";
    // The stroke is centered on the path, so we offset by half the line
    // width to keep the frame fully inside the canvas.
    roundRect(
        ctx,
        frameW / 2,
        frameW / 2,
        plateW - frameW,
        plateH - frameW,
        radius,
    );
    ctx.stroke();

    // Extremely subtle top-edge highlight on the frame - reads as a
    // single reflection line along the top, sells the glossy metal look.
    const topHl = ctx.createLinearGradient(0, 0, 0, 4 * scale);
    topHl.addColorStop(0, "rgba(255,255,255,0.25)");
    topHl.addColorStop(1, "rgba(255,255,255,0)");
    ctx.strokeStyle = topHl;
    ctx.lineWidth = 1 * scale;
    ctx.beginPath();
    ctx.moveTo(radius + 4 * scale, 2 * scale);
    ctx.lineTo(plateW - radius - 4 * scale, 2 * scale);
    ctx.stroke();
    ctx.restore();
}

// ---- Text helpers ---------------------------------------------------------

/**
 * Sum of raw glyph widths (no letter-spacing). Used by the layout code
 * to solve for the adaptive spacing that fills the white area of the
 * plate.
 */
function measureRawWidth(ctx, text) {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
        w += ctx.measureText(text[i]).width;
    }
    return w;
}

/**
 * Measure text width with an explicit letter-spacing. Kept for any
 * future caller that needs a precomputed block width. Matches exactly
 * what `renderPlateText` will draw with the same `glyphSpacing`.
 */
function measurePlateText(ctx, text, glyphSpacing) {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
        w += ctx.measureText(text[i]).width;
        if (i < text.length - 1) w += glyphSpacing;
    }
    return { width: w };
}

/**
 * Render plate text with:
 *   - Explicit letter-spacing (computed by the caller so the text
 *     stretches across the white area of the plate).
 *   - Vertical scaleY(1.2) via ctx transform - makes glyphs tall & slender.
 *   - Multi-layer emboss effect equivalent to the requested text-shadow:
 *       text-shadow:
 *         0.5px 0.5px 0px #ffffff,
 *        -0.5px -0.5px 0px #000,
 *         2px 2px 3px rgba(0,0,0,0.3);
 *     In canvas terms, layers drawn bottom-up:
 *       (a) blurred dark drop shadow (via ctx.shadow*)
 *       (b) black outline at (-0.5, -0.5)
 *       (c) white highlight at (+0.5, +0.5)
 *       (d) main black glyph on top.
 */
function renderPlateText(ctx, text, x, cy, scale, glyphSpacing) {
    // Default spacing for callers that don't pass one - matches the
    // legacy value so external callers keep working.
    if (typeof glyphSpacing !== "number") glyphSpacing = 14 * scale;
    // 0.5 px in the CSS spec maps to `0.5 * scale` for canvas resolution
    // (scale=1 means 1 canvas px == 1 CSS px at 100 %).
    const hi = 0.5 * scale;
    // The blurred shadow uses shadowOffsetX/Y - its magnitude in the CSS
    // spec is 2 px offset and 3 px blur.
    const shOff = 2 * scale;
    const shBlur = 3 * scale;

    ctx.save();
    // scaleY(1.2) around the vertical midline of the current cursor so
    // the text visually grows taller without shifting off-center.
    ctx.translate(x, cy);
    ctx.scale(1, 1.2);
    ctx.translate(-x, -cy);

    // Use `alphabetic` baseline and compute the optical vertical centre
    // of the WHOLE string from its bounding-box ascent/descent. The
    // built-in `middle` baseline uses `em` metrics that are wildly
    // inconsistent across fonts (particularly custom plate fonts like
    // Arklatrs which have loose vertical metrics), producing letters
    // that drift up or down instead of sitting on the exact centre-line
    // of the plate. Measuring the actual painted-pixel bounds fixes
    // that per-font. We measure the full string (not each glyph) so
    // every letter in the group shares the same baseline - otherwise
    // digits and letters would jitter vertically.
    ctx.textBaseline = "alphabetic";
    let baselineY = cy;
    try {
        const m = ctx.measureText(text || "M");
        const asc = m.actualBoundingBoxAscent;
        const dsc = m.actualBoundingBoxDescent;
        if (typeof asc === "number" && typeof dsc === "number" && (asc + dsc) > 0) {
            // Optical centre of the glyph mass is halfway between the
            // ascent above the baseline and the descent below. Solving
            // for the baseline that places that centre exactly at cy:
            //   baseline = cy + (ascent - descent) / 2
            baselineY = cy + (asc - dsc) / 2;
        }
    } catch {
        /* fall back to cy - better than crashing */
    }

    let cursor = x;
    for (const ch of text) {
        // (a) Blurred drop shadow - draw a translucent black glyph with
        //     canvas' built-in shadow blur. We then wipe the shadow so
        //     later layers don't inherit it.
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.3)";
        ctx.shadowOffsetX = shOff;
        ctx.shadowOffsetY = shOff;
        ctx.shadowBlur = shBlur;
        ctx.fillStyle = "rgba(0,0,0,0.9)";
        ctx.fillText(ch, cursor, baselineY);
        ctx.restore();

        // (b) Hard black outline (top-left offset).
        ctx.fillStyle = "#000000";
        ctx.fillText(ch, cursor - hi, baselineY - hi);

        // (c) White highlight (bottom-right offset).
        ctx.fillStyle = "#ffffff";
        ctx.fillText(ch, cursor + hi, baselineY + hi);

        // (d) Main black glyph on top of everything.
        ctx.fillStyle = "#0d0d10";
        ctx.fillText(ch, cursor, baselineY);

        const m = ctx.measureText(ch);
        cursor += m.width + glyphSpacing;
    }
    ctx.restore();
}

/** Draw a rounded rectangle path. */
function roundRect(ctx, x, y, w, h, r) {
    const rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.arcTo(x + w, y, x + w, y + rr, rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
    ctx.lineTo(x + rr, y + h);
    ctx.arcTo(x, y + h, x, y + h - rr, rr);
    ctx.lineTo(x, y + rr);
    ctx.arcTo(x, y, x + rr, y, rr);
    ctx.closePath();
}

/** Draw a five-point star. */
function drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
    let rot = (Math.PI / 2) * 3;
    const step = Math.PI / spikes;
    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    for (let i = 0; i < spikes; i++) {
        let x = cx + Math.cos(rot) * outerRadius;
        let y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;
        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
    }
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
}
